import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Input, Label } from "@/components/ui/Input";
import { ApiError } from "@/lib/api";
import { isIntegratorAdmin, useIntegratorMe } from "@/lib/me-query";
import { formatTimestamp } from "@/lib/utils";

import {
  DEFAULT_SALES_ORDER_PAYMENT_TERMS,
  DEFAULT_SALES_ORDER_SHIPPING_SETTINGS,
  fetchSalesOrderPaymentTermsSettings,
  fetchSalesOrderShippingSettings,
  patchSalesOrderPaymentTermsSettings,
  patchSalesOrderShippingSettings,
  SALES_ORDER_PAYMENT_TERMS_SETTINGS_QUERY_KEY,
  SALES_ORDER_SHIPPING_SETTINGS_QUERY_KEY,
  type SalesOrderPaymentTermSetting,
  type SalesOrderPaymentTermsSettingsResponse,
  type SalesOrderShippingSettingsResponse,
} from "./salesOrderPaymentTermsApi";

interface PaymentTermDraftRow extends SalesOrderPaymentTermSetting {
  id: string;
}

function createDraftRow(option?: Partial<SalesOrderPaymentTermSetting>): PaymentTermDraftRow {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    value: option?.value ?? "",
    label: option?.label ?? "",
  };
}

export function SalesOrderFormSettingsSections() {
  const queryClient = useQueryClient();
  const meQuery = useIntegratorMe();
  const canManage = isIntegratorAdmin(meQuery.data?.user.roleId);
  const settingsQuery = useQuery({
    queryKey: SALES_ORDER_PAYMENT_TERMS_SETTINGS_QUERY_KEY,
    queryFn: async (): Promise<SalesOrderPaymentTermsSettingsResponse> => {
      try {
        return await fetchSalesOrderPaymentTermsSettings();
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return {
            options: DEFAULT_SALES_ORDER_PAYMENT_TERMS,
            updatedAt: null,
          };
        }
        throw error;
      }
    },
    enabled: meQuery.isSuccess && canManage,
    staleTime: 15_000,
  });
  const shippingSettingsQuery = useQuery({
    queryKey: SALES_ORDER_SHIPPING_SETTINGS_QUERY_KEY,
    queryFn: async (): Promise<SalesOrderShippingSettingsResponse> => {
      try {
        return await fetchSalesOrderShippingSettings();
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return DEFAULT_SALES_ORDER_SHIPPING_SETTINGS;
        }
        throw error;
      }
    },
    enabled: meQuery.isSuccess && canManage,
    staleTime: 15_000,
  });

  const [draft, setDraft] = useState<PaymentTermDraftRow[]>([]);
  const [shippingSku, setShippingSku] = useState(DEFAULT_SALES_ORDER_SHIPPING_SETTINGS.sku);
  const [shippingQuantity, setShippingQuantity] = useState(String(DEFAULT_SALES_ORDER_SHIPPING_SETTINGS.quantity));
  const [shippingUnitPrice, setShippingUnitPrice] = useState(String(DEFAULT_SALES_ORDER_SHIPPING_SETTINGS.unitPrice));

  const normalizedDraft = draft.map((option) => ({
    value: option.value.trim(),
    label: option.label.trim(),
  }));

  const blankRowCount = normalizedDraft.filter((option) => option.value === "" && option.label === "").length;
  const incompleteRowCount = normalizedDraft.filter(
    (option) => (option.value === "") !== (option.label === ""),
  ).length;
  const filledRows = normalizedDraft.filter((option) => option.value !== "" && option.label !== "");
  const duplicateValueCount = (() => {
    const seen = new Set<string>();
    let duplicates = 0;
    for (const option of filledRows) {
      const key = option.value.toLowerCase();
      if (seen.has(key)) {
        duplicates += 1;
      } else {
        seen.add(key);
      }
    }
    return duplicates;
  })();
  const initialNormalized = (settingsQuery.data?.options ?? []).map((option) => ({
    value: option.value.trim(),
    label: option.label.trim(),
  }));
  const hasChanges =
    JSON.stringify(filledRows) !== JSON.stringify(initialNormalized) || blankRowCount > 0 || incompleteRowCount > 0;

  useEffect(() => {
    if (!settingsQuery.data) {
      return;
    }
    setDraft(settingsQuery.data.options.map((option) => createDraftRow(option)));
  }, [settingsQuery.data]);

  useEffect(() => {
    if (!shippingSettingsQuery.data) {
      return;
    }
    setShippingSku(shippingSettingsQuery.data.sku);
    setShippingQuantity(String(shippingSettingsQuery.data.quantity));
    setShippingUnitPrice(String(shippingSettingsQuery.data.unitPrice));
  }, [shippingSettingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (filledRows.length === 0) {
        throw new Error("Add at least one payment term.");
      }
      if (incompleteRowCount > 0) {
        throw new Error("Each payment term needs both a value and a display name.");
      }
      const seen = new Set<string>();
      for (const option of filledRows) {
        const key = option.value.toLowerCase();
        if (seen.has(key)) {
          throw new Error(`Duplicate payment-term value: ${option.value}`);
        }
        seen.add(key);
      }
      return patchSalesOrderPaymentTermsSettings(filledRows);
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.status === 404) {
        await queryClient.invalidateQueries({ queryKey: SALES_ORDER_PAYMENT_TERMS_SETTINGS_QUERY_KEY });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SALES_ORDER_PAYMENT_TERMS_SETTINGS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ["sales-orders", "payment-term-options"] });
    },
  });

  const normalizedShippingSku = shippingSku.trim();
  const normalizedShippingQuantity = Number(shippingQuantity);
  const normalizedShippingUnitPrice = Number(shippingUnitPrice);
  const shippingSettingsAreValid =
    normalizedShippingSku !== "" &&
    shippingQuantity.trim() !== "" &&
    Number.isInteger(normalizedShippingQuantity) &&
    normalizedShippingQuantity > 0 &&
    shippingUnitPrice.trim() !== "" &&
    Number.isFinite(normalizedShippingUnitPrice) &&
    normalizedShippingUnitPrice >= 0;
  const shippingSettingsHaveChanges = shippingSettingsQuery.data != null && (
    normalizedShippingSku !== shippingSettingsQuery.data.sku ||
    normalizedShippingQuantity !== shippingSettingsQuery.data.quantity ||
    normalizedShippingUnitPrice !== shippingSettingsQuery.data.unitPrice
  );
  const saveShippingMutation = useMutation({
    mutationFn: () => patchSalesOrderShippingSettings({
      sku: normalizedShippingSku,
      quantity: normalizedShippingQuantity,
      unitPrice: normalizedShippingUnitPrice,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SALES_ORDER_SHIPPING_SETTINGS_QUERY_KEY });
    },
  });
  const paymentTermsStatus = !canManage
    ? "Integrator admin access is required to edit payment terms."
    : settingsQuery.isPending
      ? "Loading settings..."
      : !hasChanges
        ? "No unsaved changes"
        : incompleteRowCount > 0
          ? "Finish both fields before saving"
          : duplicateValueCount > 0
            ? "Resolve duplicate values before saving"
            : settingsQuery.data?.updatedAt
              ? `Last saved ${formatTimestamp(settingsQuery.data.updatedAt)}`
              : settingsQuery.isSuccess
                ? "Using defaults until first save"
                : settingsQuery.isError
                  ? "Could not load settings"
                  : "Waiting to load";

  const shippingStatus = !canManage
    ? "Integrator admin access is required to edit shipping defaults."
    : shippingSettingsQuery.isPending
      ? "Loading settings..."
      : !shippingSettingsHaveChanges
        ? shippingSettingsQuery.data?.updatedAt
          ? `Last saved ${formatTimestamp(shippingSettingsQuery.data.updatedAt)}`
          : "Using defaults until first save"
        : !shippingSettingsAreValid
          ? "Finish all fields before saving"
          : "Unsaved changes";

  return (
    <>
      <HubSettingsSection
        sectionId="payment-terms"
        title="Payment terms"
        hoverHint="Display name is what users see. Value is what gets stored."
        footer={
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-600">{paymentTermsStatus}</div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setDraft((current) => [...current, createDraftRow()])}
                  disabled={!canManage}
                >
                  Add payment term
                </Button>
                <HubSaveButton
                  type="button"
                  dirty={hasChanges}
                  disabled={
                    !canManage ||
                    incompleteRowCount !== 0 ||
                    duplicateValueCount !== 0 ||
                    filledRows.length === 0
                  }
                  blockedHint="Integrator admin access is required to edit payment terms."
                  isLoading={saveMutation.isPending}
                  onClick={() => void saveMutation.mutate()}
                >
                  Save payment terms
                </HubSaveButton>
              </div>
            </div>
            {saveMutation.isError ? (
              <p className="text-sm text-rose-600">
                {saveMutation.error instanceof Error
                  ? saveMutation.error.message
                  : "Could not save payment-term settings."}
              </p>
            ) : null}
            {saveMutation.isSuccess ? (
              <p className="text-sm text-emerald-800" role="status">
                Saved. The new-order form will pick this list up automatically.
              </p>
            ) : null}
          </div>
        }
      >
        {meQuery.isPending ? <p className="text-sm text-slate-600">Loading your access level...</p> : null}
        {meQuery.isSuccess && !canManage ? (
          <p className="text-sm text-slate-600">Only integrator admins can edit payment terms and shipping defaults.</p>
        ) : null}
        {settingsQuery.isPending ? <p className="text-sm text-slate-600">Loading payment-term settings...</p> : null}
        {settingsQuery.isError ? (
          <p className="text-sm text-rose-600">
            {settingsQuery.error instanceof Error ? settingsQuery.error.message : "Could not load payment-term settings."}
          </p>
        ) : null}

        <div className="space-y-2">
          <div className="hidden grid-cols-[minmax(0,11rem)_minmax(0,1fr)_auto] gap-2 px-1 text-[11px] font-medium uppercase tracking-wide text-slate-500 md:grid">
            <div>Value</div>
            <div>Display name</div>
            <div />
          </div>
          {draft.map((option, index) => (
            <div
              key={option.id}
              className="grid gap-2 rounded-md border border-slate-200 p-2 md:grid-cols-[minmax(0,11rem)_minmax(0,1fr)_auto] md:items-end"
            >
              <div>
                <Label htmlFor={`payment-term-value-${index}`} className="md:sr-only">
                  Value
                </Label>
                <Input
                  id={`payment-term-value-${index}`}
                  value={option.value}
                  onChange={(event) =>
                    setDraft((current) =>
                      current.map((row, rowIndex) =>
                        rowIndex === index ? { ...row, value: event.target.value } : row,
                      ),
                    )
                  }
                  className="mt-1 font-mono"
                  placeholder="Net30"
                  disabled={!canManage}
                />
              </div>
              <div>
                <Label htmlFor={`payment-term-label-${index}`} className="md:sr-only">
                  Display name
                </Label>
                <Input
                  id={`payment-term-label-${index}`}
                  value={option.label}
                  onChange={(event) =>
                    setDraft((current) =>
                      current.map((row, rowIndex) =>
                        rowIndex === index ? { ...row, label: event.target.value } : row,
                      ),
                    )
                  }
                  className="mt-1"
                  placeholder="Net 30"
                  disabled={!canManage}
                />
              </div>
              <div className="flex items-end justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setDraft((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                  disabled={draft.length <= 1 || !canManage}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>

        {incompleteRowCount > 0 ? (
          <p className="text-sm text-amber-700">Finish both fields before saving any new or edited payment term.</p>
        ) : null}
        {duplicateValueCount > 0 ? (
          <p className="text-sm text-amber-700">Payment-term values must be unique.</p>
        ) : null}
      </HubSettingsSection>

      <HubSettingsSection
        sectionId="shipping"
        title="Shipping"
        hoverHint="These values are inserted when a user selects Add Shipping on a sales order."
        footer={
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-600">{shippingStatus}</div>
              <HubSaveButton
                type="button"
                dirty={shippingSettingsHaveChanges}
                disabled={!canManage || !shippingSettingsAreValid}
                blockedHint="Integrator admin access is required to edit shipping defaults."
                isLoading={saveShippingMutation.isPending}
                onClick={() => void saveShippingMutation.mutate()}
              >
                Save shipping settings
              </HubSaveButton>
            </div>
            {saveShippingMutation.isError ? (
              <p className="text-sm text-rose-600">
                {saveShippingMutation.error instanceof Error
                  ? saveShippingMutation.error.message
                  : "Could not save shipping settings."}
              </p>
            ) : null}
            {saveShippingMutation.isSuccess ? (
              <p className="text-sm text-emerald-800" role="status">
                Shipping settings saved for all sales-order users.
              </p>
            ) : null}
          </div>
        }
      >
        {shippingSettingsQuery.isPending ? (
          <p className="text-sm text-slate-600">Loading shipping settings...</p>
        ) : null}
        {shippingSettingsQuery.isError ? (
          <p className="text-sm text-rose-600">
            {shippingSettingsQuery.error instanceof Error
              ? shippingSettingsQuery.error.message
              : "Could not load shipping settings."}
          </p>
        ) : null}

        <div className="grid gap-3 md:grid-cols-[minmax(0,1.5fr)_minmax(8rem,0.6fr)_minmax(9rem,0.7fr)]">
          <div>
            <Label htmlFor="shipping-setting-sku">SKU</Label>
            <Input
              id="shipping-setting-sku"
              value={shippingSku}
              onChange={(event) => setShippingSku(event.target.value)}
              className="mt-1"
              placeholder="Shipping-O"
              disabled={!canManage}
            />
          </div>
          <div>
            <Label htmlFor="shipping-setting-quantity">Quantity</Label>
            <Input
              id="shipping-setting-quantity"
              type="number"
              min="1"
              step="1"
              value={shippingQuantity}
              onChange={(event) => setShippingQuantity(event.target.value)}
              className="mt-1"
              disabled={!canManage}
            />
          </div>
          <div>
            <Label htmlFor="shipping-setting-price">Unit price (USD)</Label>
            <Input
              id="shipping-setting-price"
              type="number"
              min="0"
              step="0.01"
              value={shippingUnitPrice}
              onChange={(event) => setShippingUnitPrice(event.target.value)}
              className="mt-1"
              disabled={!canManage}
            />
          </div>
        </div>

        {!shippingSettingsAreValid ? (
          <p className="text-sm text-amber-700">
            Enter a SKU, a positive whole-number quantity, and a unit price of zero or greater.
          </p>
        ) : null}
      </HubSettingsSection>
    </>
  );
}
